import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useSeo, SITE_URL, ORGANIZATION } from "@/lib/seo";
import { AVIS_FAQ, AVIS_INTRO } from "@/lib/faq";
import { Stars } from "@/components/site/ProductReviews";
import { ReviewCard, fmtCount, fmtNote, useShopReviews } from "@/components/site/ShopReviews";

function Distribution({ data, rating, setRating }) {
  const dist = data?.distribution || {};
  return (
    <div className="space-y-2" data-testid="reviews-distribution">
      {["5", "4", "3", "2", "1"].map((k) => {
        const n = dist[k] || 0;
        const pct = data?.count ? (n / data.count) * 100 : 0;
        const on = rating === Number(k);
        return (
          <button key={k} type="button" onClick={() => setRating(on ? null : Number(k))} data-testid={`reviews-filter-${k}`}
            className={`flex w-full items-center gap-3 rounded-sm px-2 py-1 text-left text-sm transition-colors ${on ? "bg-[rgba(217,180,106,0.15)]" : "hover:bg-white/5"}`}>
            <span className="w-14 shrink-0">{k} étoile{k === "1" ? "" : "s"}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-white/10"><span className="block h-full bg-[var(--gold)]" style={{ width: `${pct}%` }} /></span>
            <span className="w-12 shrink-0 text-right text-[var(--parchment)]/80">{fmtCount(n)}</span>
          </button>
        );
      })}
    </div>
  );
}

function AvisFaq() {
  return (
    <section data-testid="reviews-faq" className="mt-20 border-t border-[var(--line)] pt-12">
      <h2 className="font-display text-3xl sm:text-4xl">Avis Ozentya : vos questions</h2>
      <div className="mt-8 grid gap-8 md:grid-cols-2">
        {AVIS_FAQ.map((f, i) => (
          <div key={f.q} data-testid={`reviews-faq-${i}`}>
            <h3 className="font-display text-xl text-[var(--gold-soft)]">{f.q}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[var(--parchment)]/90">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const AVIS_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "WebPage", name: "Avis Ozentya", url: `${SITE_URL}/avis-clients`, about: { "@id": ORGANIZATION["@id"] }, inLanguage: "fr-FR" },
    { "@type": "FAQPage", mainEntity: AVIS_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ],
};

export default function AvisClients() {
  const [rating, setRating] = useState(null);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const { data, isFetching } = useShopReviews({ page, per_page: 24, rating: rating || undefined, q: query || undefined });
  useSeo({
    title: "Ozentya avis : 4,9/5 sur plus de 1 100 avis clients certifiés",
    description: "Avis Ozentya : lisez tous les avis certifiés des clientes, déposés après commande et collectés par Shopping Satisfaction, y compris les moins bons. Note 4,9/5.",
    path: "/avis-clients", jsonLd: AVIS_LD,
  });

  useEffect(() => { setPage(1); setItems([]); }, [rating, query]);
  useEffect(() => {
    const got = Array.isArray(data?.items) ? data.items : [];
    if (data?.page === 1) setItems(got);
    else if (data?.page > 1) setItems((prev) => (prev.length >= (data.page - 1) * 24 + got.length ? prev : [...prev, ...got]));
  }, [data]);

  return (
    <main data-testid="reviews-page" className="px-5 pb-32 pt-44 md:px-10">
      <div className="mx-auto max-w-6xl">
        <p className="eyebrow mb-4">Avis clients certifiés</p>
        <h1 className="font-display text-4xl leading-tight sm:text-5xl lg:text-6xl">Avis Ozentya : ce que nos clientes <span className="italic gold-text">disent de nous</span></h1>
        <p data-testid="reviews-intro" className="mt-6 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/90">{AVIS_INTRO}</p>
        <div className="mt-12 grid gap-10 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
          <div className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
            <p data-testid="reviews-note" className="font-display text-6xl gold-text">{fmtNote(data?.note)}<span className="text-2xl text-[var(--parchment)]/80">/5</span></p>
            <div className="mt-2"><Stars n={Math.round(data?.note || 0)} size="h-5 w-5" /></div>
            <p data-testid="reviews-count" className="mt-3 text-sm text-[var(--parchment)]/85">{fmtCount(data?.count)} avis certifiés</p>
            <div className="mt-6"><Distribution data={data} rating={rating} setRating={setRating} /></div>
            <p className="mt-6 text-xs leading-relaxed text-[var(--parchment)]/75">
              Avis déposés par des clientes après une commande sur ozentya.com et collectés par <a href={data?.source_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" data-testid="reviews-source-link">{data?.source || "Shopping Satisfaction"}</a>. Tous les avis sont affichés, y compris les moins bons, du plus récent au plus ancien.
            </p>
          </div>
          <div>
            <form onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }} className="flex gap-2">
              <label className="flex h-12 flex-1 items-center gap-2 rounded-full border border-[var(--line)] px-4 focus-within:border-[var(--gold)]">
                <Search className="h-4 w-4 text-[var(--parchment)]/70" aria-hidden="true" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher : livraison, pousse, locks…" data-testid="reviews-search-input" className="w-full bg-transparent text-sm outline-none" maxLength={60} />
              </label>
              <button type="submit" data-testid="reviews-search-button" className="h-12 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)]">Chercher</button>
            </form>
            <p data-testid="reviews-total" className="mt-4 text-sm text-[var(--parchment)]/80">
              {fmtCount(data?.total)} avis{rating ? ` à ${rating} étoile${rating > 1 ? "s" : ""}` : ""}{query ? ` contenant « ${query} »` : ""}
              {rating || query ? <button type="button" onClick={() => { setRating(null); setQ(""); setQuery(""); }} data-testid="reviews-reset" className="ml-3 underline underline-offset-4">Tout afficher</button> : null}
            </p>
            <ul className="mt-6 grid gap-5 md:grid-cols-2">
              {items.map((r, i) => <ReviewCard key={`${r.date}-${r.name}-${i}`} r={r} testid={`review-item-${i}`} />)}
            </ul>
            {data && page < data.pages ? (
              <button type="button" disabled={isFetching} onClick={() => setPage((p) => p + 1)} data-testid="reviews-load-more"
                className="mt-8 h-12 w-full rounded-full border border-[var(--gold)] text-xs font-semibold uppercase tracking-[0.2em] text-[var(--gold-soft)] transition-colors hover:bg-[rgba(217,180,106,0.1)] disabled:opacity-60">
                {isFetching ? "Chargement…" : "Voir plus d'avis"}
              </button>
            ) : null}
          </div>
        </div>
        <AvisFaq />
      </div>
    </main>
  );
}
